import type { JSX } from 'react';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { ArrowLeftOutlined, CheckCircleFilled, CloseCircleFilled, FileTextOutlined, PaperClipOutlined, PlusOutlined, SendOutlined, WarningFilled } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { openingApi, type RoomInspectionAnswer } from '../lib/wikiApi';
import { roomFolderLink } from '../lib/room-files';
import {
  recomputeRoom,
  techMin as catTechMin,
  techLabel as catTechLabel,
  type RoomCriterion as Criterion,
} from '../lib/roomCatalog';
import { LeaseContractForm } from '../components/LeaseContractWidget';
import { PlanForm } from '../components/OpeningPlanWidget';
import { ErrorList, RoomVideoField } from '../components/RoomVideoField';
import { designDueLine, fmtSize } from '../components/opening-tasks/utils';
import { uploadRoomVideos, useRoomVideos } from '../components/useRoomVideos';
import { OPENING_TASKS_KEYS } from '../lib/api/openingTasks';
import {
  MAX_ROOM_PHOTOS,
  photoBaseName,
  planPicked,
  submitErrorText,
  unreadablePhotoMessage,
} from '../lib/room-media';

/** Статус заявки осмотра → ярлык + цвет для партнёра. */
function inspectionStatusView(status: string, stageCode: string | null): { label: string; color: string; bg: string } {
  if (status === 'REJECTED' || stageCode === 'rejected') return { label: 'Отклонено', color: '#C62828', bg: 'rgba(229,57,53,.10)' };
  // УК убрала помещение в резерв (для второй-третьей точки) — не отказ и не одобрение.
  if (stageCode === 'design_sent') return { label: 'Проект в работе', color: '#5B2BA8', bg: 'rgba(114,46,209,.12)' };
  if (stageCode === 'reserve') return { label: 'В резерве УК', color: '#7A5A00', bg: 'rgba(251,140,0,.14)' };
  if (status === 'APPROVED' || status === 'CONVERTED' || stageCode === 'approved') return { label: 'Одобрено', color: '#2E7D32', bg: 'rgba(46,125,50,.12)' };
  if (status === 'UNDER_REVIEW' || stageCode === 'curator_review' || stageCode === 'site_visit') return { label: 'На проверке', color: '#B26A00', bg: 'rgba(255,145,0,.14)' };
  return { label: 'Отправлено', color: '#1565C0', bg: 'rgba(21,101,192,.10)' };
}

/**
 * «Анализ помещения» — балльная оценка по рубрикатору сети (Teamly/Google-таблица
 * «Анализ помещений»). Два рейтинга:
 *  1) Территориально-коммерческий — влияет на коммерческий успех (вес × баллы).
 *  2) Инвестиционный — уровень вложений на этапе строительства (сумма = 100).
 * Плюс технические стоп-факторы стандарта (площадь/мощность/потолки/вентиляция/
 * вода/отопление): если не выполнены — помещение не согласуется независимо от
 * баллов. Партнёр выбирает вариант по каждому критерию → внизу % рейтинга и
 * вердикт. v1 — черновик в localStorage; отправка заявки в УК — следующим шагом.
 */


const LS_KEY = 'fk-room-analysis-v1';
function load(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}
function parseNum(v?: string): number | null {
  if (!v) return null;
  const n = parseFloat(v.replace(',', '.').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : null;
}
const fmt = (n: number) => (Math.round(n * 10) / 10).toString().replace('.', ',');

interface RoomPhoto { name: string; dataUrl: string }

/**
 * Сжимает изображение на клиенте до ширины ≤1600px и JPEG q=0.72 —
 * чтобы payload не раздувался (фото с телефона 4-8 МБ → ~200-400 КБ).
 * Возвращает data-URL. Если браузер не смог открыть файл как картинку (HEIC в Chrome,
 * видео, чужой формат) — reject: исходный файл в заявку НЕ отдаём (раньше отдавали,
 * и тело запроса раздувалось видеороликом, заявка падала по лимиту 25 МБ).
 */
function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read'));
    reader.onload = () => {
      const src = String(reader.result);
      const img = new Image();
      img.onerror = () => reject(new Error('not-an-image'));
      img.onload = () => {
        // ≤1280px + JPEG q0.6 — заметно меньше тело (важно при 20-30 фото,
        // иначе суммарный payload упирается в лимит nginx). ~120-200 КБ/фото.
        const maxW = 1280;
        const scale = img.width > maxW ? maxW / img.width : 1;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('no-canvas'));
        ctx.drawImage(img, 0, 0, w, h);
        const out = canvas.toDataURL('image/jpeg', 0.6);
        // Пустой холст (слишком большая картинка) даёт «data:,» — это не фото.
        if (!out.startsWith('data:image/')) return reject(new Error('empty-canvas'));
        resolve(out);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Выбранное в поле «Фото»: картинки сжимаем, видео отдаём в блок «Видео помещения»
 * (`onVideos`), остальное и нечитаемые картинки — ошибкой, а не фото с сырым содержимым.
 * `free` — сколько мест под фото осталось.
 */
async function collectPhotos(
  picked: File[],
  free: number,
  onVideos: (videos: File[]) => void,
): Promise<{ photos: RoomPhoto[]; errors: string[] }> {
  const plan = planPicked(picked, free);
  if (plan.videos.length) onVideos(plan.videos);
  const photos: RoomPhoto[] = [];
  const errors = [...plan.errors];
  for (const f of plan.photos) {
    try {
      photos.push({ name: photoBaseName(f.name), dataUrl: await compressImage(f) });
    } catch {
      errors.push(unreadablePhotoMessage(f.name));
    }
  }
  return { photos, errors };
}

export function RoomReadinessPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Мои ранее отправленные заявки (статус + решение/комментарий куратора).
  const myQuery = useQuery({
    queryKey: ['wiki', 'my-room-inspections'],
    queryFn: () => openingApi.myRoomInspections(),
    staleTime: 30_000,
    retry: false,
  });
  const myInspections = myQuery.data ?? [];
  // Ориентир аренды по кластерам населения (реальные данные сети).
  const rentBenchQuery = useQuery({
    queryKey: ['wiki', 'rent-benchmark'],
    queryFn: () => openingApi.rentBenchmark(),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const rentClusters = rentBenchQuery.data?.clusters ?? [];
  // Каталог критериев — ЕДИНЫЙ источник истины (бэк). Форма и пересчёт строятся
  // по нему; тот же каталог использует CRM-редактор куратора.
  const catQuery = useQuery({
    queryKey: ['wiki', 'room-criteria'],
    queryFn: () => openingApi.roomCriteria(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const cat = catQuery.data ?? null;
  const [rentCluster, setRentCluster] = useState<string>('');
  const rentMoney = (n: number): string => `${Math.round(n).toLocaleString('ru-RU')} ₽`;
  const [editId, setEditId] = useState<string | null>(null);
  // sel[key] = выбранный label (для критериев) или значение (для tech-числа) или 'yes'/'no' (tech-bool).
  const [sel, setSel] = useState<Record<string, string>>(load);
  const set = (key: string, val: string): void => {
    setSel((prev) => {
      const next = { ...prev, [key]: val };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Пересчёт рейтинга по каталогу — тем же алгоритмом, что в CRM-редакторе.
  const calc = useMemo(() => (cat ? recomputeRoom(cat, sel) : null), [cat, sel]);

  // ── Отправка заявки куратору ──
  const [sending, setSending] = useState(false);
  // Заявка уже ушла, грузим видео в её переписку (кнопка: «Загружаем видео…»).
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  // Заявка ушла, а видео — нет: заявка у куратора, ролик прикладывают в переписке.
  const [videoNotice, setVideoNotice] = useState<string | null>(null);
  const [sentId, setSentId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<RoomPhoto[]>([]);
  const [photoErrors, setPhotoErrors] = useState<string[]>([]);
  const video = useRoomVideos();

  // Каталог ещё грузится — заглушка (все хуки выше уже вызваны, правило хуков ок).
  if (!cat || !calc) {
    return (
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '48px 0', textAlign: 'center', color: 'var(--fk-text-muted)' }}>
        Загружаем критерии…
      </div>
    );
  }

  // Производные для рендера/отправки (cat и calc гарантированно есть).
  const commScore = calc.commercial.reduce((s, a) => s + a.points, 0);
  const commMax = cat.commMax;
  const invScore = calc.investmentScore;
  const commPct = calc.commercialPercent;
  const techFails = calc.techFails;
  const hasStop = techFails.length > 0;
  const verdict = hasStop
    ? { label: cat.stopVerdict, color: 'var(--fk-error)', Icon: CloseCircleFilled, desc: `Стоп-фактор: ${techFails.join('; ')}.` }
    : commPct >= 80 ? { label: calc.verdict, color: 'var(--fk-success)', Icon: CheckCircleFilled, desc: 'Высокий коммерческий потенциал.' }
    : commPct >= 60 ? { label: calc.verdict, color: 'var(--fk-success)', Icon: CheckCircleFilled, desc: 'Достойный вариант.' }
    : commPct >= 40 ? { label: calc.verdict, color: 'var(--fk-warning)', Icon: WarningFilled, desc: 'Есть слабые места — взвесьте риски.' }
    : { label: calc.verdict, color: 'var(--fk-error)', Icon: WarningFilled, desc: 'Низкий потенциал — лучше поискать другое.' };

  const addPhotos = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    const added = await collectPhotos(Array.from(files), MAX_ROOM_PHOTOS - photos.length, (vs) => void video.add(vs));
    setPhotoErrors(added.errors);
    if (added.photos.length) setPhotos((prev) => [...prev, ...added.photos].slice(0, MAX_ROOM_PHOTOS));
  };
  const removePhoto = (idx: number): void => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (): Promise<void> => {
    const addr = (sel.__addr ?? '').trim();
    if (!addr) {
      setSendErr('Укажите адрес помещения перед отправкой.');
      return;
    }
    setSendErr(null);
    setVideoNotice(null);
    setSending(true);
    let submissionId: string;
    try {
      // Ответы и баллы берём из единого пересчёта по каталогу (calc).
      const res = await openingApi.submitRoomInspection({
        address: addr,
        listingUrl: (sel.__listing ?? '').trim() || null,
        verdict: verdict.label,
        commercialPercent: commPct,
        investmentScore: invScore,
        techFails,
        commercial: calc.commercial as RoomInspectionAnswer[],
        investment: calc.investment as RoomInspectionAnswer[],
        tech: calc.tech as RoomInspectionAnswer[],
        comment: (sel.__comment ?? '').trim() || null,
        photos,
      });
      submissionId = res.submissionId;
    } catch (e) {
      setSendErr(submitErrorText(e, 'Не удалось отправить заявку. Попробуйте позже.'));
      setSending(false);
      return;
    }
    // Заявка уже у куратора. Видео уходит отдельно — сообщением в её переписку (не в JSON:
    // ролики в сотни МБ в тело заявки не влезают). Сбой видео заявку не отменяет.
    let notice: string | null = null;
    if (video.videos.length) {
      setUploadingVideo(true);
      notice = await uploadRoomVideos(submissionId, video.videos, video.limits);
      setUploadingVideo(false);
      void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.thread(submissionId) });
    }
    setVideoNotice(notice);
    setSentId(submissionId);
    setSentOk(true);
    // Сбрасываем форму — партнёр уже делает новую заявку: чистим черновик,
    // фото, видео и localStorage. Обновляем список «Мои заявки».
    setSel({});
    setPhotos([]);
    setPhotoErrors([]);
    video.clear();
    try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
    void qc.invalidateQueries({ queryKey: ['wiki', 'my-room-inspections'] });
    void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks });
    setSending(false);
  };

  const renderCriteria = (list: Criterion[]): JSX.Element => (
    <div className="fk-card" style={{ overflow: 'hidden', padding: 0 }}>
      {list.map((c, i) => (
        <div key={c.key} style={{ padding: '13px 16px', borderTop: i > 0 ? '1px solid var(--fk-border)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: c.hint ? 2 : 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fk-text)' }}>{c.label}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--fk-text-muted)' }}>вес {fmt(c.weight)}</span>
          </div>
          {c.hint && <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginBottom: 8, lineHeight: 1.4 }}>{c.hint}</div>}
          {/* Ориентир аренды по кластеру населения — на реальных данных сети. */}
          {c.key === 'rent' && rentClusters.length > 0 && (
            <div style={{ marginBottom: 10, background: 'var(--fk-bg-subtle)', borderRadius: 'var(--fk-radius-md)', padding: '10px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 6 }}>
                Ориентир по населению города (медиана оборота сети × {rentBenchQuery.data?.rentPercent ?? 5}%)
              </div>
              <select
                value={rentCluster}
                onChange={(e) => setRentCluster(e.target.value)}
                style={{ width: '100%', maxWidth: 360, border: '1px solid var(--fk-border)', borderRadius: 8, padding: '8px 10px', fontFamily: 'inherit', fontSize: 13.5, outline: 'none', background: 'var(--fk-surface)' }}
              >
                <option value="">Выберите население города…</option>
                {rentClusters.map((cl) => (
                  <option key={cl.key} value={cl.key} disabled={cl.recommendedRent == null}>
                    {cl.label}
                    {cl.recommendedRent != null
                      ? ` — до ${rentMoney(cl.recommendedRent)}/мес`
                      : ' — нет данных'}
                  </option>
                ))}
              </select>
              {(() => {
                const cl = rentClusters.find((x) => x.key === rentCluster);
                if (!cl || cl.recommendedRent == null) return null;
                return (
                  <div style={{ fontSize: 12.5, color: 'var(--fk-text)', marginTop: 8, lineHeight: 1.45 }}>
                    Рекомендованная аренда для города «{cl.label}»: <b style={{ color: 'var(--fk-orange)' }}>не более {rentMoney(cl.recommendedRent)}/мес</b>.
                    <span style={{ color: 'var(--fk-text-muted)' }}> Медиана оборота таких точек ~{rentMoney(cl.medianRevenue ?? 0)}/мес (по {cl.pointsCount} точкам сети).</span>
                  </div>
                );
              })()}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {c.options.map((o) => {
              const on = sel[c.key] === o.label;
              return (
                <button key={o.label} type="button" onClick={() => set(c.key, o.label)}
                  style={{ padding: '5px 11px', borderRadius: 'var(--fk-radius-pill)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    border: `1px solid ${on ? 'transparent' : 'var(--fk-border)'}`, background: on ? 'var(--fk-orange)' : 'var(--fk-surface)', color: on ? '#fff' : 'var(--fk-text-2)' }}>
                  {o.label} <b style={{ opacity: 0.75 }}>· {fmt(o.points)}</b>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const sectionHead = (title: string, right: string): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '20px 0 8px' }}>
      <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--fk-indigo)', margin: 0 }}>{title}</h2>
      <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--fk-orange)' }}>{right}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      <button type="button" onClick={() => navigate(-1)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', marginBottom: 14, borderRadius: 'var(--fk-radius-md)', border: '1px solid var(--fk-border-strong)', background: 'var(--fk-surface)', color: 'var(--fk-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
        <ArrowLeftOutlined /> Назад
      </button>

      <h1 className="fk-page-title">Анализ помещения</h1>
      <p className="fk-page-sub">Выберите вариант по каждому критерию — внизу появится рейтинг. Черновик сохраняется в этом браузере.</p>

      {/* Мои отправленные заявки — статус и решение куратора (в т.ч. отклонение). */}
      {myInspections.length > 0 && (
        <div className="fk-card fk-card-pad" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--fk-indigo)', marginBottom: 10 }}>
            Мои заявки на помещения ({myInspections.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myInspections.map((it) => {
              const sv = inspectionStatusView(it.status, it.stageCode);
              const rejected = sv.label === 'Отклонено';
              return (
                <div key={it.id} style={{ border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fk-text)', flex: 1, minWidth: 0 }}>{it.address}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: sv.color, background: sv.bg, padding: '3px 10px', borderRadius: 'var(--fk-radius-pill)', whiteSpace: 'nowrap' }}>{sv.label}</span>
                  </div>
                  {it.verdict && <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginTop: 2 }}>{it.verdict}</div>}
                  {designDueLine(it.designDueAt) && <div style={{ fontSize: 12.5, color: 'var(--fk-text-2)', marginTop: 4, fontWeight: 600 }}>{designDueLine(it.designDueAt)}</div>}
                  {rejected && it.adminComment && (
                    <div style={{ fontSize: 12.5, color: '#C62828', marginTop: 6, background: 'rgba(229,57,53,.07)', borderRadius: 8, padding: '7px 10px' }}>
                      <b>Причина отклонения:</b> {it.adminComment}
                    </div>
                  )}
                  {!rejected && it.adminComment && (
                    <div style={{ fontSize: 12.5, color: 'var(--fk-text-2)', marginTop: 6 }}>
                      <b>Куратор:</b> {it.adminComment}
                    </div>
                  )}
                  <button type="button" onClick={() => setEditId(it.id)}
                    style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 'var(--fk-radius-pill)', border: '1px solid var(--fk-border-strong)', background: 'var(--fk-surface)', color: 'var(--fk-text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Открыть / дополнить →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editId && (
        <RoomEditPanel
          id={editId}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); void qc.invalidateQueries({ queryKey: ['wiki', 'my-room-inspections'] }); }}
        />
      )}

      {/* Адрес + объявление */}
      <div className="fk-card fk-card-pad" style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 6 }}>Адрес помещения</div>
        <input value={sel.__addr ?? ''} onChange={(e) => set('__addr', e.target.value)} placeholder="Город, улица, дом"
          style={{ width: '100%', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', padding: '9px 12px', fontFamily: 'inherit', fontSize: 14, outline: 'none', marginBottom: 10 }} />
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 6 }}>Ссылка на объявление</div>
        <input value={sel.__listing ?? ''} onChange={(e) => set('__listing', e.target.value)} placeholder="https:// (авито/циан и т.п.)"
          style={{ width: '100%', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', padding: '9px 12px', fontFamily: 'inherit', fontSize: 14, outline: 'none' }} />
      </div>

      {sectionHead('Территориально-коммерческий рейтинг', `${fmt(commScore)} / ${fmt(commMax)} (${commPct}%)`)}
      {renderCriteria(cat.commercial)}

      {sectionHead('Инвестиционный рейтинг', `${fmt(invScore)} / 100`)}
      {renderCriteria(cat.investment)}

      {sectionHead('Технические требования (стоп-факторы)', techFails.length ? '⚠ есть нарушения' : '')}
      <div className="fk-card" style={{ overflow: 'hidden', padding: 0 }}>
        {cat.tech.map((t, i) => {
          const n = parseNum(sel[t.key]);
          const min = catTechMin(cat, sel, t);
          const bad = n != null && ((min != null && n < min) || (t.max != null && n > t.max));
          return (
            <div key={t.key} style={{ padding: '12px 16px', borderTop: i > 0 ? '1px solid var(--fk-border)' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fk-text)' }}>{catTechLabel(cat, sel, t)}</span>
              <input value={sel[t.key] ?? ''} onChange={(e) => set(t.key, e.target.value)} placeholder={t.unit} inputMode="decimal"
                style={{ width: 120, border: `1px solid ${bad ? 'var(--fk-error)' : 'var(--fk-border)'}`, borderRadius: 8, padding: '6px 10px', fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
            </div>
          );
        })}
        {cat.techBool.map((t) => (
          <div key={t.key} style={{ padding: '12px 16px', borderTop: '1px solid var(--fk-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fk-text)' }}>{t.label}</span>
            <div style={{ display: 'flex', gap: 5 }}>
              {[['yes', 'Да', 'var(--fk-success)'], ['no', 'Нет', 'var(--fk-error)']].map(([v, lbl, col]) => {
                const on = sel[t.key] === v;
                return (
                  <button key={v} type="button" onClick={() => set(t.key, v)}
                    style={{ padding: '5px 14px', borderRadius: 'var(--fk-radius-pill)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${on ? 'transparent' : 'var(--fk-border)'}`, background: on ? col : 'var(--fk-surface)', color: on ? '#fff' : 'var(--fk-text-2)' }}>
                    {lbl}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Итог */}
      <div className="fk-card fk-card-pad" style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
        <verdict.Icon style={{ fontSize: 34, color: verdict.color }} />
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: verdict.color }}>{verdict.label}</div>
          <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 2 }}>{verdict.desc}</div>
          <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 4 }}>Инвестиционный рейтинг: {fmt(invScore)} / 100</div>
        </div>
      </div>

      {/* Комментарий куратору */}
      <div className="fk-card fk-card-pad" style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 6 }}>Комментарий куратору (необязательно)</div>
        <textarea value={sel.__comment ?? ''} onChange={(e) => set('__comment', e.target.value)} rows={3} placeholder="Что важно знать про это помещение"
          style={{ width: '100%', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', padding: '9px 12px', fontFamily: 'inherit', fontSize: 14, outline: 'none', resize: 'vertical' }} />
      </div>

      {/* Фото помещения */}
      {!sentOk && (
        <div className="fk-card fk-card-pad" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 8 }}>
            Фото помещения {photos.length > 0 && <span style={{ color: 'var(--fk-text-muted)' }}>· {photos.length}/{MAX_ROOM_PHOTOS}</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position: 'relative', width: 92, height: 92, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--fk-border)' }}>
                <img src={p.dataUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" onClick={() => removePhoto(i)} title="Удалить"
                  style={{ position: 'absolute', top: 3, right: 3, width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.55)', color: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1, display: 'grid', placeItems: 'center', fontFamily: 'inherit' }}>×</button>
              </div>
            ))}
            {photos.length < MAX_ROOM_PHOTOS && (
              <label style={{ width: 92, height: 92, borderRadius: 6, border: '1.5px dashed var(--fk-border-strong)', display: 'grid', placeItems: 'center', cursor: 'pointer', color: 'var(--fk-text-muted)', fontSize: 12, textAlign: 'center', gap: 2 }}>
                <PlusOutlined style={{ fontSize: 18 }} />
                <span>Добавить</span>
                <input type="file" accept="image/*" multiple capture="environment" style={{ display: 'none' }}
                  onChange={(e) => { void addPhotos(e.target.files); e.target.value = ''; }} />
              </label>
            )}
          </div>
          <ErrorList errors={photoErrors} />
          <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', marginTop: 8 }}>
            Фасад, вход, зал, санузел, вытяжка, счётчики. Фото сохранятся в заявке и загрузятся на Google Диск УК. Видео добавляйте ниже, в блоке «Видео помещения».
          </div>
        </div>
      )}

      {/* Видео помещения: не в JSON заявки, а сообщением в её переписку после отправки */}
      {!sentOk && (
        <div className="fk-card fk-card-pad" style={{ marginTop: 12 }}>
          <RoomVideoField state={video} disabled={sending} />
        </div>
      )}

      {/* Отправка заявки куратору */}
      {sentOk ? (
        <div className="fk-card fk-card-pad" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <CheckCircleFilled style={{ fontSize: 26, color: 'var(--fk-success)' }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--fk-success)' }}>Заявка отправлена куратору</div>
            <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 2 }}>Форма очищена. Статус и решение увидите выше в «Мои заявки на помещения».</div>
            {videoNotice && (
              <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: 'var(--fk-warning-text)', background: 'var(--fk-warning-soft)', borderRadius: 'var(--fk-radius-md)', padding: '8px 12px', marginTop: 10, lineHeight: 1.45, wordBreak: 'break-word' }}>
                {videoNotice}
                {sentId && (
                  <>
                    {' '}
                    <Link to={`/tasks?room=${sentId}`} style={{ color: 'inherit', textDecoration: 'underline', whiteSpace: 'nowrap' }}>Открыть переписку →</Link>
                  </>
                )}
              </div>
            )}
          </div>
          <button type="button" onClick={() => { setSentOk(false); setSendErr(null); setVideoNotice(null); setSentId(null); window.scrollTo(0, 0); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 'var(--fk-radius-md)', border: 'none', background: 'var(--fk-orange)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            <PlusOutlined /> Новая заявка
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {sendErr && (
            <div style={{ fontSize: 13, color: 'var(--fk-error)', marginBottom: 10, fontWeight: 600 }}>{sendErr}</div>
          )}
          <button type="button" onClick={() => void submit()} disabled={sending || video.preparing}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 'var(--fk-radius-md)', border: 'none', background: sending || video.preparing ? 'var(--fk-border-strong)' : 'var(--fk-orange)', color: '#fff', fontSize: 14.5, fontWeight: 700, cursor: sending || video.preparing ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            <SendOutlined /> {uploadingVideo ? 'Загружаем видео…' : sending ? 'Отправляем…' : 'Отправить куратору'}
          </button>
          {uploadingVideo && (
            <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 8 }}>
              Заявка уже у куратора, догружаем видео ({fmtSize(video.videos.reduce((sum, v) => sum + v.file.size, 0))}) — не закрывайте страницу.
            </div>
          )}
          <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 10, lineHeight: 1.5 }}>
            Данные придут заявкой в CRM «Заявки партнёров» с рейтингом и подсветкой стоп-факторов. Куратор увидит проставленные баллы и решит по помещению.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Просмотр уже отправленной заявки осмотра + догрузка фото / смена адреса,
 * ссылки, комментария → повторная отправка. Критерии/баллы партнёр менять не
 * может (только смотрит) — править их вправе менеджер в CRM. Повторная отправка
 * помечает заявку в CRM (editCount/lastEditNote).
 */
function RoomEditPanel({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['wiki', 'room-inspection', id],
    queryFn: () => openingApi.getRoomInspection(id),
    retry: false,
  });
  const data = q.data as
    | (Record<string, unknown> & {
        id: string;
        status: string;
        stageCode: string | null;
        adminComment: string | null;
      })
    | undefined;

  const [address, setAddress] = useState('');
  const [listingUrl, setListingUrl] = useState('');
  const [comment, setComment] = useState('');
  const [newPhotos, setNewPhotos] = useState<RoomPhoto[]>([]);
  const [photoErrors, setPhotoErrors] = useState<string[]>([]);
  const video = useRoomVideos();
  const [saving, setSaving] = useState(false);
  // Правки сохранены, идёт догрузка видео в переписку (кнопка: «Загружаем видео…»).
  const [uploadingVideo, setUploadingVideo] = useState(false);
  // Правки сохранены, а видео не загрузилось: панель остаётся с пояснением, повторно «Отправить»
  // не даём — фото ушли бы на диск второй раз.
  const [videoNotice, setVideoNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [inited, setInited] = useState(false);

  // Инициализируем поля из загруженной заявки один раз.
  useEffect(() => {
    if (data && !inited) {
      setAddress(typeof data.address === 'string' ? data.address : '');
      setListingUrl(typeof data.listingUrl === 'string' ? data.listingUrl : '');
      setComment(typeof data.comment === 'string' ? data.comment : '');
      setInited(true);
    }
  }, [data, inited]);

  const addPhotos = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;
    const added = await collectPhotos(Array.from(files), MAX_ROOM_PHOTOS - newPhotos.length, (vs) => void video.add(vs));
    setPhotoErrors(added.errors);
    if (added.photos.length) setNewPhotos((prev) => [...prev, ...added.photos].slice(0, MAX_ROOM_PHOTOS));
  };

  const submit = async (): Promise<void> => {
    setSaving(true);
    setErr(null);
    try {
      await openingApi.updateRoomInspection(id, {
        address: address.trim() || undefined,
        listingUrl: listingUrl.trim() || null,
        comment: comment.trim() || null,
        photos: newPhotos.length ? newPhotos : undefined,
      });
    } catch (e) {
      setErr(submitErrorText(e, 'Не удалось отправить повторно'));
      setSaving(false);
      return;
    }
    if (!video.videos.length) {
      onSaved();
      return;
    }
    // Правки уже сохранены. Видео — отдельно, сообщением в переписку заявки; сбой не отменяет сохранённое.
    setUploadingVideo(true);
    const notice = await uploadRoomVideos(id, video.videos, video.limits);
    setUploadingVideo(false);
    void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.thread(id) });
    if (!notice) {
      onSaved();
      return;
    }
    setNewPhotos([]);
    setPhotoErrors([]);
    video.clear();
    setVideoNotice(notice);
    setSaving(false);
  };
  // После частичного успеха закрытие тоже обновляет список «Мои заявки».
  const close = videoNotice ? onSaved : onClose;

  const commercial = (data?.commercial as RoomInspectionAnswer[] | undefined) ?? [];
  const investment = (data?.investment as RoomInspectionAnswer[] | undefined) ?? [];
  const tech = (data?.tech as RoomInspectionAnswer[] | undefined) ?? [];
  const folder = roomFolderLink(data);
  const photoLinks: unknown[] = Array.isArray(data?.photoLinks) ? (data?.photoLinks as unknown[]) : [];
  const photoCountLabel = photoLinks.length ? ` (${photoLinks.length})` : '';
  const commercialPercent = typeof data?.commercialPercent === 'number' ? data.commercialPercent : null;
  const investmentScore = typeof data?.investmentScore === 'number' ? data.investmentScore : null;
  const verdictText = typeof data?.verdict === 'string' ? data.verdict : '';
  // Одобренная/в проектировании: правки просто сохраняются (статус не слетает),
  // кнопка = «Сохранить». Иначе (отклонена/черновик) — «Отправить повторно».
  const isApprovedNow =
    data?.status === 'APPROVED' || data?.status === 'CONVERTED' ||
    ['approved', 'plan_attached', 'design_sent', 'project_review', 'project_approved', 'tech_plan_ready'].includes(data?.stageCode ?? '');
  const inputStyle: CSSProperties = {
    width: '100%', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)',
    padding: '9px 12px', fontFamily: 'inherit', fontSize: 14, outline: 'none',
  };
  const labelStyle: CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--fk-text-2)', marginBottom: 6 };

  const renderAnswers = (title: string, list: RoomInspectionAnswer[]): JSX.Element | null => {
    if (!list.length) return null;
    return (
      <div style={{ marginTop: 12 }}>
        <div style={labelStyle}>{title}</div>
        <div className="fk-card" style={{ overflow: 'hidden', padding: 0 }}>
          {list.map((a, i) => (
            <div key={a.key} style={{ padding: '8px 14px', borderTop: i > 0 ? '1px solid var(--fk-border)' : 'none', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', background: a.isCritical ? 'rgba(229,57,53,.06)' : undefined }}>
              <span style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>{a.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: a.isCritical ? 'var(--fk-error)' : 'var(--fk-text)', textAlign: 'right' }}>
                {a.value}{typeof a.points === 'number' ? <span style={{ color: 'var(--fk-text-muted)', fontWeight: 500 }}> · {a.points}</span> : null}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      onClick={close}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,12,40,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="fk-card"
        style={{ width: '100%', maxWidth: 640, padding: 0, overflow: 'hidden', boxShadow: '0 18px 60px rgba(0,0,0,.28)' }}>
        {/* Шапка */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--fk-border)', position: 'sticky', top: 0, background: 'var(--fk-surface)', zIndex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--fk-text)' }}>Заявка на помещение</div>
          <button type="button" onClick={close} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--fk-border)', background: 'var(--fk-surface)', cursor: 'pointer', fontSize: 16, lineHeight: 1, color: 'var(--fk-text-2)', fontFamily: 'inherit' }}>×</button>
        </div>

        <div style={{ padding: 18, maxHeight: '78vh', overflowY: 'auto' }}>
          {q.isLoading && <div style={{ fontSize: 14, color: 'var(--fk-text-muted)' }}>Загружаем…</div>}
          {q.isError && <div style={{ fontSize: 14, color: 'var(--fk-error)' }}>Не удалось загрузить заявку.</div>}
          {data && (
            <>
              {/* Вердикт + баллы (только просмотр) */}
              <div className="fk-card fk-card-pad" style={{ marginBottom: 4 }}>
                {verdictText && <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--fk-text)' }}>{verdictText}</div>}
                <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 4 }}>
                  {commercialPercent != null && <>Коммерческий рейтинг: {commercialPercent}%</>}
                  {investmentScore != null && <> · Инвестиционный: {Math.round(investmentScore * 10) / 10} / 100</>}
                </div>
                {data.adminComment && (
                  <div style={{ fontSize: 12.5, color: 'var(--fk-error)', marginTop: 6, fontWeight: 600 }}>
                    Решение куратора: {String(data.adminComment)}
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', marginTop: 8 }}>
                  Баллы и критерии править нельзя — это делает куратор. Вы можете догрузить фото и видео, уточнить адрес/ссылку/комментарий и отправить повторно.
                </div>
              </div>

              {/* Действия по одобренной заявке — НАВЕРХУ, сразу под вердиктом,
                  чтобы при открытии заполненной заявки партнёр сразу их видел,
                  а не искал под формой. Сохраняются сами (отдельные запросы) —
                  кнопку «Отправить повторно» для них нажимать НЕ нужно. */}
              {(data.status === 'APPROVED' || data.status === 'CONVERTED' ||
                ['approved', 'plan_attached', 'design_sent', 'project_review', 'project_approved', 'tech_plan_ready'].includes(data.stageCode ?? '')) && (
                <ProjectPlanSection id={id} />
              )}
              {(data.status === 'APPROVED' || data.status === 'CONVERTED' || data.stageCode === 'approved') && (
                <LeaseContractSection id={id} />
              )}

              {renderAnswers('Территориально-коммерческий рейтинг', commercial)}
              {renderAnswers('Инвестиционный рейтинг', investment)}
              {renderAnswers('Технические требования', tech)}

              {/* Папка с фото: Google Диск (новые заявки), «выгружаются» пока папки нет, Яндекс.Диск (старые) */}
              {folder.kind === 'google' && folder.url && (
                <a href={folder.url} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 13, fontWeight: 600, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
                  📁 Папка с фото на Google Диске{photoCountLabel} →
                </a>
              )}
              {folder.kind === 'pending' && (
                <div style={{ marginTop: 12, fontSize: 13, color: 'var(--fk-text-muted)' }}>
                  📁 Фото выгружаются на Google Диск{photoCountLabel}
                </div>
              )}
              {folder.kind === 'yandex' && folder.url && (
                <a href={folder.url} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 13, fontWeight: 600, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
                  📁 Открыть папку с фото{photoCountLabel} →
                </a>
              )}
              {folder.legacyUrl && (
                <div style={{ marginTop: 6 }}>
                  <a href={folder.legacyUrl} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fk-info, #0096FF)', textDecoration: 'none' }}>
                    Старые фото на Яндекс.Диске →
                  </a>
                </div>
              )}

              {/* Редактируемые поля */}
              <div style={{ marginTop: 16 }}>
                <div style={labelStyle}>Адрес помещения</div>
                <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Город, улица, дом" style={{ ...inputStyle, marginBottom: 10 }} />
                <div style={labelStyle}>Ссылка на объявление</div>
                <input value={listingUrl} onChange={(e) => setListingUrl(e.target.value)} placeholder="https:// (авито/циан и т.п.)" style={{ ...inputStyle, marginBottom: 10 }} />
                <div style={labelStyle}>Комментарий куратору</div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Что важно знать про это помещение" style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {/* Догрузка фото */}
              <div style={{ marginTop: 16 }}>
                <div style={labelStyle}>
                  Добавить фото {newPhotos.length > 0 && <span style={{ color: 'var(--fk-text-muted)' }}>· +{newPhotos.length}</span>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {newPhotos.map((p, i) => (
                    <div key={i} style={{ position: 'relative', width: 84, height: 84, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--fk-border)' }}>
                      <img src={p.dataUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button type="button" onClick={() => setNewPhotos((prev) => prev.filter((_, j) => j !== i))} title="Удалить"
                        style={{ position: 'absolute', top: 3, right: 3, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.55)', color: '#fff', cursor: 'pointer', fontSize: 13, lineHeight: 1, display: 'grid', placeItems: 'center', fontFamily: 'inherit' }}>×</button>
                    </div>
                  ))}
                  {newPhotos.length < MAX_ROOM_PHOTOS && (
                    <label style={{ width: 84, height: 84, borderRadius: 6, border: '1.5px dashed var(--fk-border-strong)', display: 'grid', placeItems: 'center', cursor: 'pointer', color: 'var(--fk-text-muted)', fontSize: 11.5, textAlign: 'center', gap: 2 }}>
                      <PlusOutlined style={{ fontSize: 16 }} />
                      <span>Добавить</span>
                      <input type="file" accept="image/*" multiple capture="environment" style={{ display: 'none' }}
                        onChange={(e) => { void addPhotos(e.target.files); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
                <ErrorList errors={photoErrors} />
                <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)', marginTop: 8 }}>
                  Новые фото добавятся в заявку и на Google Диск УК (старые сохранятся). Видео добавляйте ниже.
                </div>
              </div>

              {/* Видео: после сохранения правок уходит сообщением в переписку заявки */}
              {!videoNotice && (
                <div style={{ marginTop: 16 }}>
                  <RoomVideoField state={video} disabled={saving} title="Добавить видео" />
                </div>
              )}

              {err && <div style={{ fontSize: 13, color: 'var(--fk-error)', marginTop: 12, fontWeight: 600 }}>{err}</div>}

              {videoNotice && (
                <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: 'var(--fk-warning-text)', background: 'var(--fk-warning-soft)', borderRadius: 'var(--fk-radius-md)', padding: '8px 12px', marginTop: 12, lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {videoNotice}{' '}
                  <Link to={`/tasks?room=${id}`} style={{ color: 'inherit', textDecoration: 'underline', whiteSpace: 'nowrap' }}>Открыть переписку →</Link>
                </div>
              )}
              {uploadingVideo && (
                <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 10 }}>
                  Правки сохранены, догружаем видео ({fmtSize(video.videos.reduce((sum, v) => sum + v.file.size, 0))}) — не закрывайте страницу.
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
                {!videoNotice && (
                  <button type="button" onClick={() => void submit()} disabled={saving || video.preparing}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 'var(--fk-radius-md)', border: 'none', background: saving || video.preparing ? 'var(--fk-border-strong)' : 'var(--fk-orange)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving || video.preparing ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                    <SendOutlined /> {uploadingVideo ? 'Загружаем видео…' : saving ? (isApprovedNow ? 'Сохраняем…' : 'Отправляем…') : (isApprovedNow ? 'Сохранить' : 'Отправить повторно')}
                  </button>
                )}
                <button type="button" onClick={close} disabled={saving}
                  style={{ padding: '10px 20px', borderRadius: 'var(--fk-radius-md)', border: '1px solid var(--fk-border-strong)', background: 'var(--fk-surface)', color: 'var(--fk-text)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Закрыть
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Блок B: секция «Передать чертёж/план в УК» прямо в карточке заявки —
 * сворачиваемая обёртка над общим PlanForm (тот же в статье БЗ). По умолчанию
 * раскрыта — это основное действие партнёра после одобрения помещения.
 */
function ProjectPlanSection({ id }: { id: string }): JSX.Element {
  const [open, setOpen] = useState(true);
  return (
    <div className="fk-card" style={{ marginTop: 16, padding: 0, overflow: 'hidden', border: '1px solid var(--fk-border)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
          border: 'none', background: 'rgba(0,150,255,.06)', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 14, fontWeight: 800, color: 'var(--fk-text)', textAlign: 'left',
        }}>
        <PaperClipOutlined style={{ color: 'var(--fk-info, #0096FF)' }} />
        Передать чертёж/план в УК
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: 'var(--fk-text-muted)' }}>
          {open ? 'скрыть' : 'приложить →'}
        </span>
      </button>
      {open && (
        <div style={{ padding: 14 }}>
          <PlanForm id={id} />
        </div>
      )}
    </div>
  );
}

/**
 * Блок A: секция «Договор аренды» в карточке одобренной заявки — сворачиваемая
 * обёртка над общим компонентом LeaseContractForm (тот же используется в статье
 * базы знаний «Подписать договор аренды»).
 */
function LeaseContractSection({ id }: { id: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="fk-card" style={{ marginTop: 16, padding: 0, overflow: 'hidden', border: '1px solid var(--fk-border)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
          border: 'none', background: 'rgba(0,150,255,.06)', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 14, fontWeight: 800, color: 'var(--fk-text)', textAlign: 'left',
        }}>
        <FileTextOutlined style={{ color: 'var(--fk-info, #0096FF)' }} />
        Договор аренды
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: 'var(--fk-text-muted)' }}>
          {open ? 'скрыть' : 'оформить →'}
        </span>
      </button>
      {open && (
        <div style={{ padding: 14 }}>
          <LeaseContractForm id={id} />
        </div>
      )}
    </div>
  );
}
